import { LOGIN_PAGE_URL, MY_APP_URL, PASSWORD_SET_PAGE_URL } from "@/config";
import type { AuthUser } from "@/features/auth";
import { getMe, getUser } from "@/features/auth";
import { getOrganization } from "@/features/organization";
import { useAuthStore } from "@/store/auth/authUserStore";
import { ErrorCode } from "@/utils/errorCode";
import storage from "@/utils/storage";
import { isObject } from "@/utils/typeGuard";
import type { AxiosError } from "axios";
import type { Auth } from "firebase/auth";
import { signInWithCustomToken } from "firebase/auth";
import jwt_decode from "jwt-decode";
import React, { useContext, useEffect, useState } from "react";
import { FetchError } from "./fetchClient";
import { auth } from "./firebase";

type AuthContextType = {
  user: AuthUser | null;
  isLoading: boolean;
};

interface DecodedToken {
  user_id: string;
}

const AuthContext = React.createContext<AuthContextType | null>(null);

const storagePrefix = import.meta.env["VITE_REACT_APP_STORAGE_PREFIX"];

const getUserData = async (userId: string) => {
  const data = await getUser({ userId });
  const organization = await getOrganization().catch((orgError) => {
    if (orgError instanceof FetchError) {
      const detail = orgError.detail;
      if (
        isObject(detail) &&
        "error" in detail &&
        detail["error"] === ErrorCode.PASSWORD_EXPIRED
      ) {
        return { isPasswordExpired: true };
      }
    }
    throw orgError;
  });

  if ("isPasswordExpired" in organization) {
    return { data, error: null, isInitialLogin: true };
  }

  const { role } = await getMe();

  return {
    data: {
      ...data,
      organization: {
        ...data.organization,
        ...organization,
      },
      role,
    },
    error: null,
    isInitialLogin: false,
  };
};

const userFn = async () => {
  const token = await auth.currentUser?.getIdToken();
  if (!token) {
    return { data: null, error: null, isInitialLogin: false };
  }

  try {
    const decodedToken = jwt_decode(token) as DecodedToken;
    return await getUserData(decodedToken.user_id);
  } catch (error) {
    const axiosError = error as AxiosError;
    if (axiosError.response?.status === 401) {
      storage.clearToken();
    }
    return { data: null, error: axiosError, isInitialLogin: false };
  }
};

function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { setUser: zustandSetUser } = useAuthStore();

  const useMockAuth =
    import.meta.env["VITE_REACT_APP_FIREBASE_USE_MOCK_AUTH"] === "true";

  const redirectToLogin = () => {
    // ログイン後は必ずトップページに遷移する
    const url = new URL(LOGIN_PAGE_URL);
    url.searchParams.set("redirect", window.location.origin);
    window.location.href = url.toString();
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (firebaseUser) => {
      if (useMockAuth) {
        setIsLoading(true);
        try {
          const { data } = await getUserData("1");

          const mockUser: AuthUser = {
            ...data,
            id: "1",
            email: "dev@recerqa.com",
            first_name: "太郎",
            last_name: "山田",
            avatar_name: "yamada_taro",
            apikey: "rqa_0123456789abcdef",
            role: "superadmin",
          };

          setUser(mockUser);
          zustandSetUser(mockUser);
        } catch (error) {
          // 組織情報の取得に失敗した場合は処理を続行
        } finally {
          setIsLoading(false);
        }
        return;
      }

      setIsLoading(true);

      if (firebaseUser) {
        try {
          const { data, error, isInitialLogin } = await userFn();

          setUser(data);
          zustandSetUser(data);

          if (!data || error) {
            throw error;
          }

          if (isInitialLogin) {
            // 初回ログイン時の時はパスワード設定画面にリダイレクト
            window.location.replace(
              `${PASSWORD_SET_PAGE_URL}?redirect=${MY_APP_URL}/views/`
            );

            setIsLoading(false);
            return;
          }
        } catch (error) {
          await auth.signOut();
          redirectToLogin();
        }
      } else {
        const tokenName = `${storagePrefix}token`;
        const token = document.cookie
          .split("; ")
          .find((row) => row.startsWith(tokenName))
          ?.split("=")[1];

        if (!token) {
          redirectToLogin();

          setIsLoading(false);
          return;
        }

        try {
          await signInWithCustomToken(auth as Auth, token);
        } catch (error) {
          redirectToLogin();
        }
      }

      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [useMockAuth, zustandSetUser]);

  const contextValue = {
    user,
    isLoading,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
}

function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  if (context === null) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export { AuthProvider, useAuth };
